/**
 * useContactSync - Hook para sincronização em tempo real de identificadores WhatsApp
 *
 * Escuta eventos Socket.io de sync de contatos e atualiza o cache do React Query
 * automaticamente quando o Backend recebe resposta do Gateway.
 */

import { useEffect, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS } from '@troia-v3/types';
import type {
  ContactIdentifiersSyncedPayload,
  ContactSyncFailedPayload
} from '@troia-v3/types';

export interface UseContactSyncOptions {
  /** ID do contato para filtrar eventos */
  contactId?: string;
  /** Callback quando sync é concluído com sucesso */
  onSyncSuccess?: (payload: ContactIdentifiersSyncedPayload) => void;
  /** Callback quando sync falha */
  onSyncFailed?: (payload: ContactSyncFailedPayload) => void;
  /** Habilitar/desabilitar listeners */
  enabled?: boolean;
}

export interface UseContactSyncReturn {
  /** Status da conexão Socket.io */
  isConnected: boolean;
  /** Indica se há um sync em andamento */
  isSyncing: boolean;
  /** Último erro de sync (se houver) */
  lastError: string | null;
}

/**
 * Hook para gerenciar sincronização de identificadores WhatsApp em tempo real
 *
 * @example
 * ```tsx
 * const { isSyncing, lastError } = useContactSync({
 *   contactId: '670f3c8e9a8b4c001234abcd',
 *   onSyncSuccess: (payload) => {
 *     toast.success('WhatsApp sincronizado!');
 *     console.log('Identifiers:', payload.identifiers);
 *     console.log('Avatar:', payload.avatarUrl);
 *   },
 *   onSyncFailed: (payload) => {
 *     toast.error(payload.error);
 *   }
 * });
 * ```
 */
export const useContactSync = (options: UseContactSyncOptions = {}): UseContactSyncReturn => {
  const {
    contactId,
    onSyncSuccess,
    onSyncFailed,
    enabled = true,
  } = options;

  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket();
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  /**
   * Handler para evento de sync bem-sucedido
   */
  const handleSyncSuccess = useCallback((payload: ContactIdentifiersSyncedPayload) => {
    // Filtrar apenas eventos do contato específico (se fornecido)
    if (contactId && payload.contactId !== contactId) {
      return;
    }

    setIsSyncing(false);
    setLastError(null);

    // Atualizar cache do React Query - Contact individual
    queryClient.setQueryData(['contact', payload.contactId], (oldData: any) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        identifiers: {
          ...oldData.identifiers,
          whatsapp: payload.identifiers || []
        },
        ...(payload.avatarUrl && { picture: payload.avatarUrl })
      };
    });

    // Invalidar lista de contatos para refresh
    queryClient.invalidateQueries({
      queryKey: ['contacts'],
      exact: false
    });

    // Callback customizado
    if (onSyncSuccess) {
      onSyncSuccess(payload);
    }
  }, [contactId, onSyncSuccess, queryClient]);

  /**
   * Handler para evento de sync falhado
   */
  const handleSyncFailed = useCallback((payload: ContactSyncFailedPayload) => {
    // Filtrar apenas eventos do contato específico (se fornecido)
    if (contactId && payload.contactId !== contactId) {
      return;
    }

    setIsSyncing(false);
    setLastError(payload.error);

    // Callback customizado
    if (onSyncFailed) {
      onSyncFailed(payload);
    }

    console.error('❌ [useContactSync] Sync failed for:', payload.contactId, payload.error);
  }, [contactId, onSyncFailed]);

  /**
   * Registrar listeners Socket.io
   */
  useEffect(() => {
    if (!enabled || !socket || !isConnected) {
      return;
    }

    // Registrar listeners
    socket.on(SOCKET_EVENTS.CONTACT_IDENTIFIERS_SYNCED, handleSyncSuccess);
    socket.on(SOCKET_EVENTS.CONTACT_SYNC_FAILED, handleSyncFailed);

    // Cleanup ao desmontar
    return () => {
      socket.off(SOCKET_EVENTS.CONTACT_IDENTIFIERS_SYNCED, handleSyncSuccess);
      socket.off(SOCKET_EVENTS.CONTACT_SYNC_FAILED, handleSyncFailed);
    };
  }, [enabled, socket, isConnected, handleSyncSuccess, handleSyncFailed]);

  /**
   * Marca como "syncing" quando contato é criado (útil para loading states)
   */
  useEffect(() => {
    if (contactId && enabled) {
      setIsSyncing(true);
      setLastError(null);

      // Auto-reset após 30 segundos (timeout)
      const timeout = setTimeout(() => {
        setIsSyncing(false);
        setLastError('Sync timeout - verifique a conexão do Gateway');
      }, 30000);

      return () => clearTimeout(timeout);
    }
  }, [contactId, enabled]);

  return {
    isConnected,
    isSyncing,
    lastError,
  };
};
